import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';

// custom hooks
import { useModulePermissions } from 'hooks/useModulePermissions';

// constants
import { MODULES } from 'constants/modules';

// material-ui
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';

// icons
import {
  IconEdit,
  IconInfoCircle,
  IconUser,
  IconBriefcase,
  IconMapPin,
  IconPhone,
  IconMail,
  IconFileDescription,
  IconUsers,
  IconShieldCheck
} from '@tabler/icons-react';

// API imports
import {
  getAgreementGrievanceListService,
  updateAgreementGrievanceService,
  AgreementGrievanceItem,
  GrievanceForm
} from 'api/agreement-grievance';

// ==============================|| AGREEMENT GRIEVANCE MASTER ||============================== //

export default function AgreementGrievanceMaster() {
  const moduleId = MODULES.AGREEMENT_GRIEVANCE.id;

  // Get permissions for this module
  const { hasEditPermission } = useModulePermissions(moduleId);

  const { enqueueSnackbar } = useSnackbar();
  const [grievanceData, setGrievanceData] = useState<AgreementGrievanceItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState<GrievanceForm>({
    grievanceOfficerName: '',
    designation: '',
    address: '',
    contactNo: '',
    emailId: ''
  });

  // Fetch grievance data on component mount
  useEffect(() => {
    fetchGrievanceData();
  }, []);

  const fetchGrievanceData = async () => {
    setLoading(true);
    try {
      const response = await getAgreementGrievanceListService();
      if (response.success && response.data.length > 0) {
        // Since it's a single record, get the first item
        setGrievanceData(response.data[0]);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch grievance officer details';
      enqueueSnackbar(msg, { variant: 'error' });
      setGrievanceData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = () => {
    if (!grievanceData) return;

    setEditForm({
      grievanceOfficerName: grievanceData.grievanceOfficerName,
      designation: grievanceData.designation,
      address: grievanceData.address,
      contactNo: grievanceData.contactNo,
      emailId: grievanceData.emailId
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditForm({
      grievanceOfficerName: '',
      designation: '',
      address: '',
      contactNo: '',
      emailId: ''
    });
  };

  const handleUpdateGrievance = async () => {
    if (!grievanceData) return;

    // Frontend validation
    if (!editForm.grievanceOfficerName.trim()) {
      enqueueSnackbar('Grievance Officer name is required', { variant: 'error' });
      return;
    }
    if (!editForm.designation.trim()) {
      enqueueSnackbar('Designation is required', { variant: 'error' });
      return;
    }
    if (!editForm.emailId.trim()) {
      enqueueSnackbar('Email is required', { variant: 'error' });
      return;
    }
    if (!editForm.contactNo.trim()) {
      enqueueSnackbar('Contact number is required', { variant: 'error' });
      return;
    }

    setUpdating(true);
    try {
      const response = await updateAgreementGrievanceService(grievanceData._id, {
        grievanceOfficerName: editForm.grievanceOfficerName.trim(),
        designation: editForm.designation.trim(),
        address: editForm.address.trim(),
        contactNo: editForm.contactNo.trim(),
        emailId: editForm.emailId.trim()
      });

      if (response.success) {
        enqueueSnackbar(response.message || 'Grievance officer details updated successfully', { variant: 'success' });
        handleCloseEditDialog();
        fetchGrievanceData(); // Refresh the data
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update grievance officer details';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        {/* Header Section */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2
            }}
          >
            <Box>
              <h2 style={{ margin: 0, marginBottom: 8, fontSize: '24px', fontWeight: 600, color: '#1a1a1a' }}>
                Agreement Grievance Master
              </h2>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Manage grievance officer details for regulatory compliance</p>
            </Box>

            {/* EDIT BUTTON — only shown when user has edit permission */}
            {hasEditPermission && (
              <Button
                variant="contained"
                startIcon={<IconEdit size={18} />}
                onClick={handleOpenEditDialog}
                disabled={loading || !grievanceData}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#2c3e50',
                  '&:hover': {
                    backgroundColor: '#1a252f'
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc'
                  },
                  px: 3,
                  py: 1
                }}
              >
                Edit Details
              </Button>
            )}
          </Box>
        </Grid>

        {/* Info Banner */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 2.5,
              backgroundColor: '#e3f2fd',
              border: '1px solid #90caf9',
              borderRadius: '8px'
            }}
          >
            <IconInfoCircle size={20} style={{ color: '#1976d2', marginTop: 2, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <div style={{ fontSize: '14px', color: '#1565c0', fontWeight: 600, marginBottom: 8 }}>About Agreement Grievance Master</div>
              <div style={{ fontSize: '13px', color: '#1565c0', marginBottom: 12 }}>
                This information is used in customer-facing documents and regulatory compliance. Changes will reflect immediately in:
              </div>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <IconFileDescription size={16} style={{ color: '#1565c0' }} />
                  <span style={{ fontSize: '13px', color: '#1565c0' }}>Loan Agreements</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <IconUsers size={16} style={{ color: '#1565c0' }} />
                  <span style={{ fontSize: '13px', color: '#1565c0' }}>Customer Disclosures</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <IconShieldCheck size={16} style={{ color: '#1565c0' }} />
                  <span style={{ fontSize: '13px', color: '#1565c0' }}>NBFC Compliance</span>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Main Content Card */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              border: '1px solid #e0e0e0',
              boxShadow: 'none',
              borderRadius: '8px'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {/* Section Header */}
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box>
                  <h3 style={{ margin: 0, marginBottom: 4, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>
                    Grievance Officer Details
                  </h3>
                  <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                    Information displayed in loan agreements and customer disclosures
                  </p>
                </Box>
                <Chip
                  label="Single Record"
                  size="small"
                  sx={{
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    fontWeight: 500,
                    fontSize: '12px',
                    height: '24px',
                    borderRadius: '12px',
                    border: '1px solid #81c784'
                  }}
                />
              </Box>

              {/* Details Grid */}
              {loading ? (
                <Box sx={{ p: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress size={40} />
                </Box>
              ) : grievanceData ? (
                <>
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      {/* Name */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '8px',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <IconUser size={20} style={{ color: '#666' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: 4, fontWeight: 500 }}>Grievance Officer Name</div>
                            <div style={{ fontSize: '15px', color: '#1a1a1a', fontWeight: 500 }}>{grievanceData.grievanceOfficerName}</div>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Designation */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '8px',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <IconBriefcase size={20} style={{ color: '#666' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: 4, fontWeight: 500 }}>Designation</div>
                            <div style={{ fontSize: '15px', color: '#1a1a1a', fontWeight: 500 }}>{grievanceData.designation}</div>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Address */}
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '8px',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <IconMapPin size={20} style={{ color: '#666' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: 4, fontWeight: 500 }}>Address</div>
                            <div style={{ fontSize: '15px', color: '#1a1a1a', lineHeight: 1.6 }}>{grievanceData.address}</div>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Contact Number */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '8px',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <IconPhone size={20} style={{ color: '#666' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: 4, fontWeight: 500 }}>Contact Number</div>
                            <div style={{ fontSize: '15px', color: '#1a1a1a', fontWeight: 500, fontFamily: 'monospace' }}>
                              {grievanceData.contactNo}
                            </div>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Email */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '8px',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <IconMail size={20} style={{ color: '#666' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: 4, fontWeight: 500 }}>Email ID</div>
                            <div style={{ fontSize: '15px', color: '#1a1a1a', fontWeight: 500 }}>{grievanceData.emailId}</div>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      p: 2.5,
                      borderTop: '1px solid #f0f0f0',
                      backgroundColor: '#fafafa',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconFileDescription size={16} style={{ color: '#999' }} />
                      <span style={{ fontSize: '13px', color: '#666' }}>
                        {new Date(grievanceData.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}{' '}
                        at{' '}
                        {new Date(grievanceData.updatedAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </span>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box sx={{ p: 8, textAlign: 'center' }}>
                  <Box sx={{ color: '#999', fontSize: '14px' }}>No grievance officer data found</Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            borderBottom: '1px solid #e0e0e0',
            pb: 2
          }}
        >
          Edit Grievance Officer Details
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Grievance Officer Name"
                value={editForm.grievanceOfficerName}
                onChange={(e) => setEditForm({ ...editForm, grievanceOfficerName: e.target.value })}
                placeholder="Enter officer name"
                size="small"
                style={{ marginTop: '12px' }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Designation"
                value={editForm.designation}
                style={{ marginTop: '12px' }}
                onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })}
                placeholder="Enter designation"
                size="small"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                placeholder="Enter complete address"
                size="small"
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Contact Number"
                value={editForm.contactNo}
                onChange={(e) => setEditForm({ ...editForm, contactNo: e.target.value })}
                placeholder="Enter contact number"
                size="small"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email ID"
                value={editForm.emailId}
                onChange={(e) => setEditForm({ ...editForm, emailId: e.target.value })}
                placeholder="Enter email address"
                size="small"
                type="email"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #e0e0e0'
          }}
        >
          <Button
            onClick={handleCloseEditDialog}
            disabled={updating}
            sx={{
              textTransform: 'none',
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateGrievance}
            variant="contained"
            disabled={
              updating ||
              !editForm.grievanceOfficerName.trim() ||
              !editForm.designation.trim() ||
              !editForm.emailId.trim() ||
              !editForm.contactNo.trim()
            }
            sx={{
              textTransform: 'none',
              backgroundColor: '#2c3e50',
              '&:hover': {
                backgroundColor: '#1a252f'
              },
              '&:disabled': {
                backgroundColor: '#ccc'
              }
            }}
          >
            {updating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} style={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Update Details'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}